import React, { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import {
  X, ShoppingCart, Trash2, Plus, Minus, Tag, CheckCircle, Loader2,
  BookOpen, Users, Calendar, Zap, Sparkles, ArrowRight, Lock, Gift,
} from 'lucide-react'
import { useCart } from '@/contexts/CartContext'
import type { CartItem } from '@/contexts/CartContext'

const TYPE_META: Record<string, { icon: React.ComponentType<{ className?: string }>; label: string; color: string }> = {
  course:      { icon: BookOpen,  label: 'Course',       color: 'bg-violet-100 text-violet-700' },
  program:     { icon: Zap,       label: 'Program',      color: 'bg-amber-100 text-amber-700'   },
  class_pack:  { icon: Calendar,  label: 'Class Pack',   color: 'bg-blue-100 text-blue-700'     },
  study_group: { icon: Users,     label: 'Study Group',  color: 'bg-emerald-100 text-emerald-700'},
  session:     { icon: Sparkles,  label: 'Session',      color: 'bg-rose-100 text-rose-700'     },
  workshop:    { icon: Gift,      label: 'Workshop',     color: 'bg-teal-100 text-teal-700'     },
  membership:  { icon: CheckCircle, label: 'Membership', color: 'bg-orange-100 text-orange-700' },
}

function fmt(usd: number) {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(usd)
}

function CartItemRow({ item }: { item: CartItem }) {
  const { removeItem, updateQty } = useCart()
  const meta = TYPE_META[item.type] ?? TYPE_META['course']
  const Icon = meta.icon

  return (
    <motion.div
      layout
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: 20 }}
      className="flex gap-3 py-4 border-b border-sage-100 last:border-0"
    >
      {/* Image / type icon */}
      <div className="w-16 h-16 rounded-xl overflow-hidden flex-shrink-0 bg-sage-100 flex items-center justify-center relative">
        {item.imageUrl ? (
          <img src={item.imageUrl} alt={item.title} className="w-full h-full object-cover" />
        ) : (
          <Icon className="w-7 h-7 text-sage-400" />
        )}
        <span className={`absolute bottom-0 right-0 text-[8px] font-bold px-1 py-0.5 rounded-tl-lg ${meta.color}`}>{meta.label}</span>
      </div>

      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-sage-900 leading-tight truncate">{item.title}</p>
        {item.subtitle && <p className="text-xs text-sage-500 mt-0.5 truncate">{item.subtitle}</p>}
        <p className="text-[10px] text-sage-400 mt-0.5">by {item.provider.name}</p>

        <div className="flex items-center justify-between mt-2">
          {/* Qty control — for courses/sessions qty > 1 is uncommon but supported */}
          {item.type === 'class_pack' ? (
            <div className="flex items-center gap-1 bg-sage-100 rounded-lg p-0.5">
              <button onClick={() => updateQty(item.cartId, item.quantity - 1)} className="w-6 h-6 flex items-center justify-center rounded-md hover:bg-white transition-colors">
                <Minus className="w-3 h-3 text-sage-600" />
              </button>
              <span className="text-xs font-bold text-sage-900 w-5 text-center tabular-nums">{item.quantity}</span>
              <button onClick={() => updateQty(item.cartId, item.quantity + 1)} className="w-6 h-6 flex items-center justify-center rounded-md hover:bg-white transition-colors">
                <Plus className="w-3 h-3 text-sage-600" />
              </button>
            </div>
          ) : (
            <span className="text-xs text-sage-400">Qty: {item.quantity}</span>
          )}

          <div className="flex items-center gap-2">
            <span className="text-sm font-bold text-sage-900">{fmt(item.price * item.quantity)}</span>
            <button onClick={() => removeItem(item.cartId)} className="p-1 text-sage-400 hover:text-rose-500 transition-colors rounded">
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      </div>
    </motion.div>
  )
}

function CouponRow() {
  const { state, applyCoupon, removeCoupon } = useCart()
  const [input, setInput] = useState('')
  const [trying, setTrying] = useState(false)
  const [error, setError] = useState('')

  const VALID: Record<string, number> = { ROYAL10: 10, WELCOME20: 20, GURUKUL15: 15, YTT25: 25 }

  const handleApply = async () => {
    const code = input.trim().toUpperCase()
    if (!code) return
    setTrying(true); setError('')
    await new Promise(r => setTimeout(r, 600))
    if (VALID[code]) {
      applyCoupon(code)
      setInput('')
    } else {
      setError('Invalid coupon code')
    }
    setTrying(false)
  }

  if (state.couponCode) {
    return (
      <div className="flex items-center justify-between px-4 py-2 bg-emerald-50 border border-emerald-200 rounded-xl">
        <div className="flex items-center gap-2">
          <Tag className="w-4 h-4 text-emerald-600" />
          <span className="text-sm font-semibold text-emerald-700">{state.couponCode}</span>
          <span className="text-xs text-emerald-500">−{state.discountPct}% off</span>
        </div>
        <button onClick={removeCoupon} className="text-emerald-400 hover:text-emerald-700"><X className="w-4 h-4" /></button>
      </div>
    )
  }

  return (
    <div className="space-y-1">
      <div className="flex gap-2">
        <div className="relative flex-1">
          <Tag className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-sage-400" />
          <input
            type="text" placeholder="Coupon code" value={input} onChange={e => setInput(e.target.value.toUpperCase())}
            onKeyDown={e => e.key === 'Enter' && handleApply()}
            className="w-full pl-8 pr-3 py-2 text-sm border border-sage-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-amber-300 bg-white"
          />
        </div>
        <button onClick={handleApply} disabled={!input.trim() || trying}
          className="px-3 py-2 bg-sage-900 hover:bg-sage-700 text-white text-xs font-semibold rounded-xl disabled:opacity-50 transition-colors flex items-center gap-1">
          {trying ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Apply'}
        </button>
      </div>
      {error && <p className="text-xs text-rose-500 pl-1">{error}</p>}
      <p className="text-[10px] text-sage-400 pl-1">Try: ROYAL10 · WELCOME20 · GURUKUL15 · YTT25</p>
    </div>
  )
}

// Group items by provider for routing
function groupByProvider(items: CartItem[]) {
  return items.reduce((acc, item) => {
    const key = item.provider.id
    if (!acc[key]) acc[key] = { provider: item.provider, items: [] }
    acc[key].items.push(item)
    return acc
  }, {} as Record<string, { provider: CartItem['provider']; items: CartItem[] }>)
}

export default function CartDrawer() {
  const { state, closeCart, subtotal, total, itemCount } = useCart()
  const navigate = useNavigate()

  const groups = groupByProvider(state.items)
  const providerCount = Object.keys(groups).length
  const hasDiscount = state.discountPct > 0
  const savings = subtotal - total

  return (
    <>
      {/* Backdrop */}
      <AnimatePresence>
        {state.isOpen && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={closeCart}
            className="fixed inset-0 z-40 bg-black/40 backdrop-blur-sm"
          />
        )}
      </AnimatePresence>

      {/* Drawer */}
      <AnimatePresence>
        {state.isOpen && (
          <motion.div
            initial={{ x: '100%' }} animate={{ x: 0 }} exit={{ x: '100%' }}
            transition={{ type: 'spring', damping: 26, stiffness: 280 }}
            className="fixed right-0 top-0 bottom-0 z-50 w-full max-w-md bg-white shadow-2xl flex flex-col"
          >
            {/* Header */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-sage-200 bg-sage-50 flex-shrink-0">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-gradient-to-br from-amber-500 to-orange-500 rounded-xl flex items-center justify-center shadow-sm">
                  <ShoppingCart className="w-4 h-4 text-white" />
                </div>
                <div>
                  <h2 className="text-base font-bold text-sage-900">Your Cart</h2>
                  <p className="text-xs text-sage-500">{itemCount} item{itemCount !== 1 ? 's' : ''}{providerCount > 1 ? ` · ${providerCount} providers` : ''}</p>
                </div>
              </div>
              <button onClick={closeCart} className="p-1.5 text-sage-400 hover:text-sage-700 hover:bg-sage-200 rounded-lg transition-colors">
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Items list */}
            <div className="flex-1 overflow-y-auto px-5">
              {state.items.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full text-center py-20">
                  <div className="w-20 h-20 bg-sage-100 rounded-full flex items-center justify-center mb-4">
                    <ShoppingCart className="w-9 h-9 text-sage-300" />
                  </div>
                  <h3 className="text-lg font-bold text-sage-800 mb-1">Your cart is empty</h3>
                  <p className="text-sm text-sage-500 mb-6 max-w-xs">Browse courses, programs, and sessions — then add them here.</p>
                  <Link to="/store" onClick={closeCart}
                    className="inline-flex items-center gap-2 px-6 py-3 bg-amber-500 hover:bg-amber-400 text-white font-semibold rounded-xl transition-colors">
                    Browse Products <ArrowRight className="w-4 h-4" />
                  </Link>
                  <div className="mt-4 grid grid-cols-2 gap-2 w-full max-w-xs">
                    {[
                      { label: 'Courses', href: '/gurukul' },
                      { label: 'Programs', href: '/programs' },
                      { label: 'Find a Practitioner', href: '/marketplace' },
                      { label: 'Study Groups', href: '/community' },
                    ].map(s => (
                      <Link key={s.href} to={s.href} onClick={closeCart}
                        className="text-xs text-amber-600 hover:text-amber-700 text-center py-2 border border-amber-200 rounded-lg hover:bg-amber-50 transition-colors">
                        {s.label}
                      </Link>
                    ))}
                  </div>
                </div>
              ) : (
                <>
                  {/* Provider groups */}
                  {Object.values(groups).map(group => (
                    <div key={group.provider.id} className="mb-2">
                      {providerCount > 1 && (
                        <div className="flex items-center gap-2 py-2">
                          <div className="w-6 h-6 rounded-full bg-sage-200 flex items-center justify-center text-[10px] font-bold text-sage-600 flex-shrink-0">
                            {group.provider.name.charAt(0)}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-xs font-semibold text-sage-700 truncate">{group.provider.name}</p>
                            <p className="text-[10px] text-sage-400 capitalize">{group.provider.type}</p>
                          </div>
                          {group.provider.profileHref && (
                            <Link to={group.provider.profileHref} onClick={closeCart} className="text-[10px] text-amber-600 hover:underline flex-shrink-0">
                              View →
                            </Link>
                          )}
                        </div>
                      )}
                      <AnimatePresence>
                        {group.items.map(item => <CartItemRow key={item.cartId} item={item} />)}
                      </AnimatePresence>
                    </div>
                  ))}

                  {/* AI Suggestion */}
                  <div className="mt-3 mb-2 bg-gradient-to-r from-violet-50 to-indigo-50 border border-violet-200 rounded-xl p-3">
                    <div className="flex items-start gap-2">
                      <Sparkles className="w-4 h-4 text-violet-500 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-xs font-semibold text-violet-800">Royal AI Suggestion</p>
                        <p className="text-[11px] text-violet-600 mt-0.5">
                          {state.items.some(i => i.type === 'course') && !state.items.some(i => i.type === 'study_group')
                            ? 'Add a Study Group to deepen your course learning with like-minded practitioners.'
                            : state.items.some(i => i.type === 'session')
                              ? 'Pair your 1:1 session with a course for a complete transformation journey.'
                              : 'Members save 15–30% on all purchases. Consider a membership upgrade.'}
                        </p>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>

            {/* Footer — order summary + checkout */}
            {state.items.length > 0 && (
              <div className="flex-shrink-0 border-t border-sage-200 bg-white px-5 py-4 space-y-3">
                <CouponRow />

                <div className="space-y-1.5 text-sm">
                  <div className="flex justify-between text-sage-600">
                    <span>Subtotal ({itemCount} item{itemCount !== 1 ? 's' : ''})</span>
                    <span className="font-medium">{fmt(subtotal)}</span>
                  </div>
                  {hasDiscount && (
                    <div className="flex justify-between text-emerald-600">
                      <span>Discount ({state.discountPct}%)</span>
                      <span>−{fmt(savings)}</span>
                    </div>
                  )}
                  <div className="flex justify-between font-bold text-sage-900 text-base pt-1 border-t border-sage-100">
                    <span>Total</span>
                    <span>{fmt(total)}</span>
                  </div>
                </div>

                {/* Routing hint when multiple providers */}
                {providerCount > 1 && (
                  <p className="text-[11px] text-sage-400 text-center">
                    {providerCount} providers — we'll route each booking to the right teacher or studio
                  </p>
                )}

                <button
                  onClick={() => { closeCart(); navigate('/checkout') }}
                  className="w-full py-3.5 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-400 hover:to-orange-400 text-white font-bold rounded-xl shadow-lg shadow-amber-200 flex items-center justify-center gap-2 transition-all"
                >
                  <Lock className="w-4 h-4" /> Secure Checkout · {fmt(total)}
                </button>

                <div className="flex items-center justify-center gap-4 text-[10px] text-sage-400">
                  <span className="flex items-center gap-1"><CheckCircle className="w-3 h-3 text-green-400" /> 30-day guarantee</span>
                  <span className="flex items-center gap-1"><Lock className="w-3 h-3 text-sage-400" /> Secure checkout</span>
                </div>

                <Link to="/store" onClick={closeCart} className="block text-center text-xs text-amber-600 hover:text-amber-700 font-medium">
                  Continue Shopping →
                </Link>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </>
  )
}
